<template>
  <div>
    <div v-if="!hideSaveTemplate" class="grid grid-cols-3 gap-4">
      <div class="col-span-2">
        <BaseButton
          :disabled="isSaving || savingTemplate || !canSubmit"
          :class="[(isSaving || savingTemplate || !canSubmit) ? 'cursor-not-allowed' : 'cursor-pointer']"
          @click="saveBudgetItemTemplate(model)"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Save as template</span>
            <span
              v-if="savingTemplate"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </div>

    <div class="grid grid-cols-3 gap-4 mt-6">
      <div class="col-span-2">
        <form.Field
          name="title"
          :validators="{
            onChange: yup.string().required('Title is required')
          }"
        >
          <template #default="{ field }">
            <BaseInput
              v-model="model.title"
              :name="field.name"
              placeholder="Title"
              :disabled="isSaving"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @input="(e) => setTitle(e.target.value)"
            />
          </template>
        </form.Field>
        <div class="mt-4">
          <Editor
            id="budget-item-description"
            v-model="model.scopeOfWork"
            description="Scope of Work"
            :disabled="isSaving"
            :additional-options="{ placeholder: 'Scope of Work' }"
          />
        </div>
      </div>

      <div>
        <span class="select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium mb-2">Budgeted Cost</span>
        <div class="grid grid-cols-2 gap-4">
          <BaseInputCurrency
            :model-value="model.cost"
            description="Total M+L Cost"
            disabled
          />

          <div>
            <form.Field
              name="margin"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number', 'Margin must a number between 0 and 99', isValidPercentage)
              }"
            >
              <template #default="{ field }">
                <BaseInput
                  v-model.number="model.margin"
                  :name="field.name"
                  type="number"
                  description="Gross Profit Margin"
                  :disabled="isSaving"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  no-error-messages
                  @blur="field.handleChange(model.margin); updatePricesByCost()"
                />
              </template>
            </form.Field>
          </div>
        </div>

        <div class="flex gap-4">
          <form.Field
            name="unitPrice"
            :validators="{
              onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Unit Price must be a valid number', isValidAmount)
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                v-model="model.unitPrice"
                :name="field.name"
                description="Unit Price"
                :disabled="isSaving"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                no-error-messages
                @blur="field.handleChange(model.unitPrice); updateTotalPrice()"
              />
            </template>
          </form.Field>

          <div class="flex-1">
            <form.Field
              name="quantity"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number', isStrictPositiveNumber).required('Quantity is required')
              }"
            >
              <template #default="{ field }">
                <BaseInput
                  v-model.number="model.quantity"
                  :name="field.name"
                  type="number"
                  description="Quantity"
                  :disabled="isSaving"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @blur="field.handleChange(model.quantity); updateTotalPrice()"
                />
              </template>
            </form.Field>
          </div>

          <form.Field
            name="totalPrice"
            :validators="{
              onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Item Total must be a valid number', isValidAmount)
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                v-model="model.totalPrice"
                :name="field.name"
                description="Item Total"
                :disabled="isSaving"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                no-error-messages
                @blur="field.handleChange(model.totalPrice); updateUnitPrice()"
              />
            </template>
          </form.Field>
        </div>

        <!-- add is credit line item check box -->
        <div class="mt-2">
          <BaseSwitch
            v-model="model.isCreditLineItem"
            label="Is Credit Line Item"
            :disabled="isSaving"
          />
        </div>
      </div>
    </div>
    <!-- attachments -->
    <div class="flex flex-wrap gap-4 mt-4 items-center">
      <div>
        <BaseButton
          variant="secondary"
          text="Attach Files"
          type="button"
          icon-class="i-mdi-paperclip"
          @click="open = true"
        />
      </div>
      <div
        v-for="(file, index) in model.attachments"
        :key="file"
        class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
      >
        <img
          v-if="isImage(file)"
          :src="file"
          class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
          @click="expandImage(file)"
        >
        <div
          v-else
          class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
          @click="expandImage(file)"
        >
          <div class="text-dark font-weight-bold">
            {{ fileTypeToString(file) }}
          </div>
        </div>
        <span
          class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
          @click="deleteAttachment(index)"
        >
          <span class="i-mdi-close text-white"></span>
        </span>
      </div>
    </div>

    <!-- Materials and labors -->
    <div class="grid grid-cols-5 xl:grid-cols-6 gap-4 mt-6">
      <div class="col-span-2 gap-3">
        <div class="flex items-end mb-3">
          <div class="flex-1">
            <form.Field
              name="materialsBudget"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Materials Budget must be a positive number', isValidNumber).required('Materials Budget is required')
              }"
            >
              <template #default="{ field }">
                <BaseInputCurrency
                  v-model="model.materialsBudget"
                  :name="field.name"
                  label="Materials Budget"
                  :disabled="isSaving || enableMaterials"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  no-error-messages
                  @blur="field.handleChange(model.materialsBudget); updateCost()"
                />
              </template>
            </form.Field>
          </div>
          <!-- claculator button material -->
          <span
            v-show="!isSaving"
            class="mx-3 mb-[0.6rem]"
            :class="[(isSaving || enableMaterials) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer']"
            @click="(isSaving || !enableMaterials) && openCalculator('material')"
          >
            <span class="i-heroicons-calculator size-9" />
          </span>

          <MarkupCalculator
            :key="`${id}-material`"
            v-model="showMatBudMarkupCalculator"
            :initial-value="model.materialsBudget"
            @use-calculation="(model.materialsBudget = $event) && updateCost()"
          />
        </div>
        <BaseSwitch
          v-model="enableMaterials"
          label="Enable Itemized Materials"
          :disabled="isSaving"
          @change="toggleMaterials"
        />
      </div>

      <div class="col-span-2">
        <div class="flex items-end mb-3">
          <div class="flex-1">
            <form.Field
              name="laborBudget"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Labor Budget must be a positive number', isValidNumber).required('Labor Budget is required')
              }"
            >
              <template #default="{ field }">
                <BaseInputCurrency
                  v-model="model.laborBudget"
                  :name="field.name"
                  label="Labor Budget"
                  :disabled="isSaving || enableLabor"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  no-error-messages
                  @blur="field.handleChange(model.laborBudget); updateCost()"
                />
              </template>
            </form.Field>
          </div>
          <!-- claculator button for labor -->
          <span
            v-show="!isSaving"
            class="mx-3 mb-[0.6rem]"
            :class="[(isSaving || enableLabor) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer']"
            @click="(isSaving || !enableLabor) && openCalculator('labor')"
          >
            <span class="i-heroicons-calculator size-9" />
          </span>

          <MarkupCalculator
            :key="`${id}-labor`"
            v-model="showLabBudMarkupCalculator"
            :initial-value="model.laborBudget"
            @use-calculation="(model.laborBudget = $event) && updateCost()"
          />
        </div>
        <BaseSwitch
          v-model="enableLabor"
          label="Enable Itemized Labor"
          :disabled="isSaving"
          @change="toggleLabor"
        />
      </div>
    </div>

    <!-- Cost items -->
    <div class="mt-6">
      <CreateCostItem
        v-model="model"
        :is-open="isOpen"
        :enable-labor="enableLabor"
        :enable-materials="enableMaterials"
        :is-saving="isSaving"
        @calculate-total="calculateCostItemTotal"
        @can-submit="(value) => setIsCostItemsValid(value)"
      />
    </div>
  </div>

  <Modal
    v-model="open"
    title="Upload Attachments"
    width="w-full md:w-1/2"
    close-button-text="Close"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    @close="setAttachments"
  >
    <template #body>
      <div class="mt-4">
        <AppFileUpload
          :storage-path="`/budgets/${model.id}/`"
          @on-file-upload-completed="fileUploadCompleted"
        />
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { BudgetItem } from '@/models/budget'
import BaseInput from '@/components/base/BaseInput.vue'
import Editor from '@/components/TextEditor.vue'
import CreateCostItem from './CreateCostItem.vue'
import { Labor } from '@/models/labor'
import { Material } from '@/models/material'
import { extend } from 'underscore'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import MarkupCalculator from '@/components/MarkupCalculator.vue'
import { computed, onMounted, PropType, reactive, ref, watch } from 'vue'
import { toFixed } from '@/utils'
import StorageService from '@/services/StorageService'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseSwitch from '@/components/base/BaseSwitch.vue'
import { useNotification } from '@kyvg/vue3-notification'
import SettingService from '@/services/SettingService'
import BudgetTemplateService from '@/services/BudgetTemplateService'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { useMutation } from '@tanstack/vue-query'

const model = defineModel({
  type: Object as PropType<BudgetItem>,
  default: () => (new BudgetItem())
})

const props = defineProps({
  id: { type: String, required: true },
  isSaving: { type: Boolean, default: false },
  hideSaveTemplate: { type: Boolean, default: false },
  isOpen: { type: Boolean, default: true }
})

const emit = defineEmits(['onDelete', 'can-submit'])
const storageService = new StorageService()
const { notify } = useNotification()
const open = ref(false)
const defaultMargin = ref(30)
const tempAttachments = ref(model.value.attachments || [])
const budgetItemTemplateService = new BudgetTemplateService()
const enableMaterials = ref(model.value.materials.length > 0)
const enableLabor = ref(model.value.labor.length > 0)

const defaultValues = reactive({
  ...model.value,
})

const form = useForm({
  defaultValues,
  validatorAdapter: yupValidator()
})

const canSubmit = ref(false)
const isCostItemsValid = ref(false)

const emitCanSubmit = () => {
  const isValid = form.useStore(state =>  state.canSubmit)
  const isBudgetItemValid = isCostItemsValid.value && isValid.value
  if(canSubmit.value !== isBudgetItemValid) {
    canSubmit.value = isBudgetItemValid
    emit('can-submit', isBudgetItemValid)
  }
}

watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    form.validateAllFields('change')
    emitCanSubmit()
  }
})

const setIsCostItemsValid = (value) => {
  isCostItemsValid.value = value
  emitCanSubmit()
}

const setTitle = (title) => {
  model.value.title = title
  updateField('title', title, 'change')
}

const updateField = (name, value, clause) => {
  // @ts-ignore
  form.setFieldValue(name, value)
  form.validateField(name, clause)
  emitCanSubmit()
}

const isValidPercentage = (value: string) => {
  if(model.value.isCreditLineItem) return true
  return !isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 99
}

const isValidNumber = (value: string) => {
  return !isNaN(parseFloat(value)) && parseFloat(value) >= 0
}

const isValidAmount = (value: string) => {
  return !isNaN(parseFloat(value))
}

const isStrictPositiveNumber = (value) => {
  return !isNaN(Number(value)) && Number(value) > 0
}

onMounted(async () => {
  if (model.value.margin === -1) {
    try {
      defaultMargin.value = (await (new SettingService().find('generalSettings'))).defaultMargin
    } catch (_) {
      notify({
        type: 'error',
        title: 'Error',
        text: 'Failed to get default margin'
      })
    }

    if (props.isOpen) {
      model.value.margin = parseFloat(String(defaultMargin.value))
      updateField('margin', model.value.margin, 'change')
    }
  }

  await form.validateAllFields('change')
  emitCanSubmit()
})

const calculateGrossProfit = computed(() => {
  const grossProfit = (toFixed(model.value.unitPrice) - toFixed(model.value.cost)) * model.value.quantity
  return toFixed(grossProfit)
})

const calculateMargin = computed(() => {
  if (toFixed(model.value.totalPrice) == 0) return model.value.margin
  if (model.value.unitPrice < 0 && model.value.cost == (-1 * model.value.unitPrice)) return 0

  const totalPrice = model.value.totalPrice < 0 ? -1 * model.value.totalPrice : model.value.totalPrice
  const margin = calculateGrossProfit.value / toFixed(totalPrice)

  return toFixed(toFixed(margin, 4) * 100, 2)
})

const calculateUnitPrice = computed(() => {
  if ((1 - (model.value.margin / 100) == 0)) return 0

  let unitPrice = toFixed(model.value.cost) / (1 - (model.value.margin / 100))

  if (form.getFieldValue('unitPrice') < 0) {
    unitPrice = -1 * unitPrice
  }

  return toFixed(toFixed(unitPrice), 2)
})

const calculateTotalPrice = computed(() => {
  const totalPrice = toFixed(model.value.unitPrice) * model.value.quantity
  return toFixed(toFixed(totalPrice), 2)
})

// ok
const updateUnitPrice = () => {
  if (model.value.quantity <= 0) {
    model.value.quantity = 1
    updateField('quantity', model.value.quantity, 'change')
  }

  const unitPrice = (toFixed(model.value.totalPrice) / model.value.quantity)
  model.value.unitPrice = toFixed(toFixed(unitPrice), 2)
  model.value.margin = calculateMargin.value

  updateField('unitPrice', model.value.unitPrice, 'change')
  updateField('margin', model.value.margin, 'change')
}
// ok
const updateTotalPrice = () => {
  if (model.value.quantity <= 0) {
    model.value.quantity = 1
    updateField('quantity', model.value.quantity, 'change')
  }

  const totalPrice = (toFixed(model.value.unitPrice)) * model.value.quantity
  model.value.totalPrice = toFixed(toFixed(totalPrice), 2)
  model.value.margin = calculateMargin.value

  updateField('totalPrice', model.value.totalPrice, 'change')
  updateField('margin', model.value.margin, 'change')
}
// ok
const updatePricesByCost = () => {
  if (model.value.cost > 0) {
    model.value.margin = model.value.margin >= 0 ? model.value.margin : defaultMargin.value
    model.value.unitPrice = calculateUnitPrice.value
    model.value.totalPrice = calculateTotalPrice.value
    updateField('margin', model.value.margin, 'change')
  } else {
    model.value.unitPrice = 0
    model.value.totalPrice = 0
  }

  updateField('unitPrice', model.value.unitPrice, 'change')
  updateField('totalPrice', model.value.totalPrice, 'change')
}
// ok
const updateCost = () => {
  let total = 0
  const materialsBudget = toFixed(model.value.materialsBudget) || 0
  const laborBudget = toFixed(model.value.laborBudget) || 0

  total = toFixed(materialsBudget + laborBudget)
  model.value.cost = total
  updateField('cost', model.value.cost, 'change')
  updatePricesByCost()
  return total
}
// ok
const calculateCostItemTotal = (item: BudgetItem) => {
  model.value.materialsBudget = item.materialsBudget
  model.value.laborBudget = item.laborBudget
  model.value.cost = item.cost

  updateField('materialsBudget', model.value.materialsBudget, 'change')
  updateField('laborBudget', model.value.laborBudget, 'change')
  updateField('cost', model.value.cost, 'change')

  updatePricesByCost()
}

const toggleMaterials = () => {
  if (!enableMaterials.value) {
    model.value.materials = []
    model.value.materialsBudget = 0
    updateField('materialsBudget', model.value.materialsBudget, 'change')
  } else {
    if (model.value.materials.length === 0) {
      const material = new Material()
      material.id = ''
      material.quantity = 1
      model.value.materials.push(extend({}, material))
    }
  }
}

const toggleLabor = () => {
  if (!enableLabor.value) {
    model.value.labor = []
    model.value.laborBudget = 0
    updateField('laborBudget', model.value.laborBudget, 'change')
  } else {
    if (model.value.labor.length === 0) {
      const labor = new Labor()
      labor.id = ''
      labor.hours = 1
      model.value.labor.push(extend({}, labor))
    }
  }
}

const fileUploadCompleted = (urls: string[]) => {
  tempAttachments.value.push(...urls)
}

const setAttachments = () => {
  open.value = false
  model.value.attachments = tempAttachments.value
}

const expandImage = (imageURL: string) => {
  window.open(imageURL, '_blank')
}

const { mutateAsync: saveBudgetItemTemplate, isPending: savingTemplate } = useMutation({
  mutationFn: (BudgetItem: BudgetItem) => budgetItemTemplateService.create(BudgetItem),
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Success',
      text: 'Budget item template created successfully'
    })
  },
  onError: (error) => {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to create budget item template'
    })
  }
})

const showMatBudMarkupCalculator = ref(false)
const showLabBudMarkupCalculator = ref(false)
const openCalculator = (materialOrLabor: string) => {
  if (materialOrLabor === 'material') {
    showMatBudMarkupCalculator.value = true
  } else {
    showLabBudMarkupCalculator.value = true
  }
}

const deleteAttachment = async (index) => {
  const attachment = model.value.attachments[index]
  try {
    await storageService.deleteAttachmentWithStyledImages(attachment)
    model.value.attachments.splice(index, 1)
    tempAttachments.value = tempAttachments.value.filter(att => att !== attachment)
  } catch (error) {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to delete attachment'
    })
  }
}

const isImage = (file: string) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext[1].toLowerCase()) : false
}

const fileTypeToString = (file) => {
  const ext = file.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ext[1].toUpperCase() : 'FILE'
}

</script>
